import React from 'react';
import { takeEvery, put, select, all } from 'redux-saga/effects';
import { toastr } from 'react-redux-toastr';

import { loaderDecorator } from './decorators/loader-decorator';

import * as types from '../actions/vacancies/types';
import * as actions from '../actions/vacancies';
import * as schoolActions from '../actions/school';
import { getSchoolVacancies } from '../actions/school';
import { setListSettings } from '../actions/list-settings';

import PostVacancyErrorMessage from '../components/common/post-vacancy-error-message';
import RePostVacancyErrorMessage from '../components/common/re-post-vacancy-error-message';

import listNames from '../constants/list-names';
import vacancyStatuses from '../constants/vacancy-statuses';
import httpStatusCodes from '../constants/http-status-codes';
import applicationSubstatuses from '../constants/application-substatuses';

import apiFetcher from '../tools/api-fetcher';
import history from '../tools/history';
import queryStringify from '../tools/query-stringify';
import { parseHttpStatusText, getListSettingsFromQueryParams } from '../tools';
import handleError from '../tools/error-helper';
import formDataEncode from '../tools/form-data-encode';
import { openPrintVersion } from '../tools/print-helper';

function* reloadVacancyContextData(schoolId, vacancyId) {
  const vacancyDetails = yield select(state => state.vacancyDetails);
  const schoolVacancies = yield select(state => state.schoolVacancies);

  if (vacancyDetails.get('vacancySummary').get('vacancyId') === vacancyId) {
    yield put(actions.getVacancySummary(schoolId, vacancyId));
  }

  if (schoolVacancies.get('results').toJS().length) {
    yield put(getSchoolVacancies(schoolVacancies.get('queryParams')));
  }
}

function* getVacancyQuestions({ schoolId, vacancyId, questionsType }) {
  yield put(actions.setQuestionsLoading(true, questionsType));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(
        `/schools/${schoolId}/vacancies/${vacancyId}/questions-set/${questionsType}`
      );

    yield put(actions.setVacancyQuestions(data, questionsType));
  } catch (exception) {
    handleError(
      exception,
      `Failed to load vacancy ${questionsType} questions.`
    );
  }

  yield put(actions.setQuestionsLoading(false, questionsType));
}

function* addVacancyQuestion({ schoolId, vacancyId, questionType, question }) {
  yield put(actions.setQuestionAdding(true, questionType));
  yield put(actions.setQuestionsLoading(true, questionType));

  try {
    yield apiFetcher
      .auth()
      .post(
        `/schools/${schoolId}/vacancies/${vacancyId}/questions-set/${questionType}`,
        question
      );

    yield put(actions.getVacancyQuestions(schoolId, vacancyId, questionType));

    toastr.success(
      `New vacancy ${questionType} question was successfully added.`
    );
  } catch (exception) {
    handleError(
      exception,
      `Failed to add new vacancy ${questionType} question.`
    );
  }

  yield put(actions.setQuestionAdding(false, questionType));
  yield put(actions.setQuestionsLoading(false, questionType));
}

function* removeVacancyQuestion({
  schoolId,
  vacancyId,
  questionType,
  questionId
}) {
  yield put(actions.setQuestionsLoading(true, questionType));
  yield put(actions.setQuestionDeleting(true, questionType));

  try {
    yield apiFetcher
      .auth()
      .delete(
        `/schools/${schoolId}/vacancies/${vacancyId}/questions-set/${questionType}/${questionId}`
      );

    yield put(actions.getVacancyQuestions(schoolId, vacancyId, questionType));

    toastr.success(
      `Vacancy ${questionType} question was successfully removed.`
    );
  } catch (exception) {
    handleError(
      exception,
      `Failed to remove vacancy ${questionType} question.`
    );
  }

  yield put(actions.setQuestionDeleting(false, questionType));
  yield put(actions.setQuestionsLoading(false, questionType));
}

function* addVacancy({ vacancy, saveType, duplicate }) {
  yield put(actions.setFormLoading(true));

  try {
    const { vacancyDetails } = vacancy;

    const { data: vacancyId } = yield apiFetcher
      .auth()
      .post(
        `schools/${vacancyDetails.schoolId}/vacancies/draft`,
        formDataEncode(vacancy)
      );

    if (duplicate) {
      yield put(
        actions.setDuplicatedVacancyReferenceData(
          vacancyDetails.schoolId,
          vacancyId
        )
      );
    }

    if (saveType === vacancyStatuses.posted) {
      yield apiFetcher
        .auth()
        .post(`schools/${vacancyDetails.schoolId}/vacancies/posted`, {
          vacancyId
        });

      yield toastr.success('Vacancy was successfuly posted.');

      if (!duplicate) {
        yield history.push(
          `/schools/${vacancyDetails.schoolId}/vacancies/${vacancyId}/invitations`
        );
      } else {
        yield put(actions.setDuplicatedVacancyHasBeenPosted(true));
      }
    } else {
      yield toastr.success('Vacancy draft was created successfuly.');
      yield history.push(
        `/schools/${vacancyDetails.schoolId}/vacancies/${vacancyId}`
      );
    }
  } catch (exception) {
    if (saveType === vacancyStatuses.posted) {
      handleError(exception, `Failed to save and post vacancy draft.`);
    } else {
      handleError(exception, `Failed to save vacancy draft.`);
    }
  }

  yield put(actions.setFormLoading(false));
}

function* getVacancySummary({ schoolId, vacancyId }) {
  yield put(actions.setSummaryLoading(true));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`/schools/${schoolId}/vacancies/${vacancyId}/summary`);

    yield put(actions.setVacancySummary(data));
  } catch (exception) {
    if (exception.response) {
      const { status, statusText } = exception.response;

      if (status === 403 || status === 404) {
        const parsedStatusText = parseHttpStatusText(statusText);

        const { text, subcode } = parsedStatusText;

        yield put(actions.setVacancySummaryError({ text, subcode }));
      } else {
        handleError(exception, `Failed to get vacancy summary.`);
      }
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setSummaryLoading(false));
}

function* updateVacancySummary({ vacancySummary }) {
  yield put(actions.setFormLoading(true));

  try {
    const { schoolId, vacancyId } = vacancySummary;
    yield apiFetcher.auth().put(`/schools/${schoolId}/vacancies/${vacancyId}`, {
      vacancyDetails: vacancySummary
    });

    yield put(actions.getVacancySummary(schoolId, vacancyId));

    yield toastr.success('Vacancy was updated successfuly.');
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(actions.setFormLoading(false));
}

function* getVacancyApplications({ schoolId, vacancyId, queryParams }) {
  yield put(actions.setApplicationsLoading(true));
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(
        `schools/${schoolId}/vacancies/${vacancyId}/applications?${queryStringify(
          { ...queryParams }
        )}`
      );

    yield put(actions.setVacancyApplicationsQueryParams(queryParams));

    yield put(actions.setVacancySelectedApplicationIds([]));

    yield put(actions.setVacancyApplications(data));

    const listSettings = yield getListSettingsFromQueryParams(queryParams);
    
    yield put(setListSettings(listNames.schoolVacancyApplications, listSettings));
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(actions.setApplicationsLoading(false));
}

function* postVacancy({ schoolId, vacancyId }) {
  yield put(actions.setVacancyPosting(true));

  try {
    yield apiFetcher
      .auth()
      .post(`schools/${schoolId}/vacancies/posted`, { vacancyId });

    yield* reloadVacancyContextData(schoolId, vacancyId);

    yield toastr.success('Vacancy was successfuly posted.');

    yield history.push(
      `/schools/${schoolId}/vacancies/${vacancyId}/invitations`
    );
  } catch (exception) {
    if (exception.response) {
      const { status } = exception.response;

      switch (status) {
        case httpStatusCodes.UnprocessableEntity: {
          toastr.error('Failed to post vacancy.', {
            timeOut: 0,
            component: (
              <PostVacancyErrorMessage
                schoolId={schoolId}
                vacancyId={vacancyId}
              />
            )
          });
          break;
        }

        case httpStatusCodes.Conflict: {
          toastr.error('Vacancy cannot be posted because of its status.', {
            timeOut: 0
          });
          break;
        }

        default: {
          toastr.error('Failed to post vacancy.', { timeOut: 0 });
        }
      }
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setVacancyPosting(false));
}

function* putVacancyOnHold({ schoolId, putVacancyOnHoldRequest }) {
  yield put(actions.setVacancyPuttingOnHold(true));

  try {
    yield apiFetcher
      .auth()
      .post(`schools/${schoolId}/vacancies/on-hold`, putVacancyOnHoldRequest);

    yield* reloadVacancyContextData(
      schoolId,
      putVacancyOnHoldRequest.vacancyId
    );

    yield toastr.success('Vacancy was successfuly put on hold.');
  } catch (exception) {
    if (exception.response) {
      const { status } = exception.response;

      switch (status) {
        case httpStatusCodes.Conflict: {
          toastr.error('Vacancy cannot be put on hold because of its status.', {
            timeOut: 0
          });
          break;
        }

        default: {
          handleError(exception, 'Failed to put vacancy on hold.');
        }
      }
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setVacancyPuttingOnHold(false));
}

function* rePostVacancy({ schoolId, rePostVacancyRequest }) {
  yield put(actions.setVacancyRePosting(true));

  try {
    yield apiFetcher
      .auth()
      .post(`schools/${schoolId}/vacancies/posted`, rePostVacancyRequest);

    yield* reloadVacancyContextData(schoolId, rePostVacancyRequest.vacancyId);

    yield toastr.success('Vacancy was successfuly re-posted.');

    yield history.push(
      `/schools/${schoolId}/vacancies/${rePostVacancyRequest.vacancyId}/invitations`
    );
  } catch (exception) {
    if (exception.response) {
      const { status } = exception.response;

      switch (status) {
        case httpStatusCodes.UnprocessableEntity: {
          toastr.error('Failed to re-post vacancy.', {
            timeOut: 0,
            component: (
              <RePostVacancyErrorMessage
                schoolId={schoolId}
                vacancyId={rePostVacancyRequest.vacancyId}
              />
            )
          });
          break;
        }

        case httpStatusCodes.Conflict: {
          toastr.error('Vacancy cannot be re-posted because of its status.', {
            timeOut: 0
          });
          break;
        }

        default: {
          handleError(exception, 'Failed to re-post vacancy.');
        }
      }
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setVacancyRePosting(false));
}

function* archiveVacancy({ schoolId, vacancyId }) {
  yield put(actions.setVacancyArchiving(true));

  try {
    yield apiFetcher
      .auth()
      .post(`schools/${schoolId}/vacancies/archived`, { vacancyId });

    yield* reloadVacancyContextData(schoolId, vacancyId);

    yield toastr.success('Vacancy was successfuly archived.');
  } catch (exception) {
    if (exception.response) {
      const { status } = exception.response;

      switch (status) {
        case httpStatusCodes.Conflict: {
          toastr.error('Vacancy cannot be archived because of its status.', {
            timeOut: 0
          });
          break;
        }

        default: {
          handleError(exception, 'Failed to archive vacancy.');
        }
      }
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setVacancyArchiving(false));
}

function* deleteDraftVacancy({ schoolId, vacancyId }) {
  yield put(actions.setDraftVacancyDeleting(true));
  try {
    yield apiFetcher
      .auth()
      .delete(`schools/${schoolId}/vacancies/${vacancyId}`);
    yield* reloadVacancyContextData(schoolId, vacancyId);
    yield toastr.success('Vacancy was successfuly deleted.');
  } catch (exception) {
    handleError(exception, 'Failed to delete vacancy.');
  }

  yield put(actions.setDraftVacancyDeleting(false));
}

function* keepVacancyPosted({ schoolId, vacancyId, keepVacancyPostedRequest }) {
  yield put(actions.setVacancyKeepingPosted(true));

  try {
    yield apiFetcher
      .auth()
      .put(
        `schools/${schoolId}/vacancies/posted/${vacancyId}`,
        keepVacancyPostedRequest
      );

    yield* reloadVacancyContextData(schoolId, vacancyId);

    yield toastr.success('Vacancy was successfuly keeped posted.');
  } catch (exception) {
    if (exception.response) {
      const { status } = exception.response;

      switch (status) {
        case httpStatusCodes.Conflict: {
          toastr.error(
            'Vacancy cannot be keeped posted because of its status.',
            { timeOut: 0 }
          );
          break;
        }

        default: {
          handleError(exception, 'Failed to keep vacancy posted.');
        }
      }
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setVacancyKeepingPosted(false));
}

function* remainVacancyOnHold({
  schoolId,
  vacancyId,
  remainVacancyOnHoldRequest
}) {
  yield put(actions.setVacancyRemainingOnHold(true));

  try {
    yield apiFetcher
      .auth()
      .put(
        `schools/${schoolId}/vacancies/on-hold/${vacancyId}`,
        remainVacancyOnHoldRequest
      );

    yield* reloadVacancyContextData(schoolId, vacancyId);

    yield toastr.success('Vacancy was successfuly remained on hold.');
  } catch (exception) {
    if (exception.response) {
      const { status } = exception.response;

      switch (status) {
        case httpStatusCodes.Conflict: {
          toastr.error(
            'Vacancy cannot be remained on hold because of its status.',
            { timeOut: 0 }
          );
          break;
        }

        default: {
          handleError(exception, 'Failed to remain vacancy on hold.');
        }
      }
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setVacancyRemainingOnHold(false));
}

function* getDataForDuplicateVacancy({ schoolId, vacancyId }) {
  yield put(actions.setVacancyDuplicationFormDataLoading(true));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`schools/${schoolId}/vacancies/${vacancyId}/data-for-duplicate`);
    yield put(actions.setDuplicatedVacancy(data));
  } catch (exception) {
    handleError(exception);
  }

  yield put(actions.setVacancyDuplicationFormDataLoading(false));
}

function* getNotHiredApplicationsToInviteToDuplicatedVacancy({
  schoolId,
  vacancyId
}) {
  yield put(actions.setVacancyDuplicationFormDataLoading(true));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(
        `schools/${schoolId}/vacancies/${vacancyId}/applications?substatuses[]=${applicationSubstatuses.notHired}`
      );

    yield put(
      actions.setNotHiredApplicationsToInviteToDuplicatedVacancy(
        data.results
      )
    );
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(actions.setVacancyDuplicationFormDataLoading(false));
}

function* sendPriorCandidatesInvitations({
  schoolId,
  invitationVacancyId,
  invitations,
  onSubmitted
}) {
  try {
    yield put(actions.setInvitingPriorCandidates(true));

    yield apiFetcher
      .auth()
      .put(
        `schools/${schoolId}/vacancies/${invitationVacancyId}/invitations/to-candidates`,
        invitations
      );

    onSubmitted();
    toastr.success(`Vacancy invitation was successfully sent.`);

    const schoolPriorCandidates = yield select(
      state => state.schoolPriorCandidates
    );

    if (
      schoolPriorCandidates &&
      schoolPriorCandidates.get('results') &&
      schoolPriorCandidates.get('queryParams')
    ) {
      const queryParams = schoolPriorCandidates.get('queryParams');
      yield put(schoolActions.getSchoolPriorCandidates(schoolId, queryParams));
    }
  } catch (exception) {
    handleError(exception);
  } finally {
    yield put(actions.setInvitingPriorCandidates(false));
  }
}

function* sendByEmailsInvitations({
  schoolId,
  invitationVacancyId,
  invitations,
  onSubmitted
}) {
  try {
    yield put(actions.setInvitingByEmails(true));
    yield apiFetcher
      .auth()
      .put(
        `schools/${schoolId}/vacancies/${invitationVacancyId}/invitations/via-email`,
        invitations
      );

    onSubmitted();
    toastr.success(
      `Vacancy invitation was successfully sent to ${invitations.emails
        .length} email address(es).`
    );
  } catch (exception) {
    handleError(exception);
  } finally {
    yield put(actions.setInvitingByEmails(false));
  }
}

function* printVacancyApplications({
  url,
  selectedApplicationIds,
  printOptions
}) {
  try {
    yield openPrintVersion(url, {
      postData: {
        applicationIds: selectedApplicationIds,
        ...(printOptions || {})
      }
    });
  } catch (exception) {
    handleError(
      exception,
      'Failed to load vacancy applications print version.'
    );
  }
}

function takeEveryWithLoading(type, targetFunction, loader) {
  return takeEvery(type, loaderDecorator(targetFunction, loader));
}

export default function* vacanciesSaga() {
  yield all([
    takeEvery(types.VACANCIES_ADD_VACANCY, addVacancy),
    takeEvery(types.VACANCIES_UPDATE_SUMMARY, updateVacancySummary),
    takeEvery(types.VACANCY_GET_QUESTIONS, getVacancyQuestions),
    takeEvery(types.VACANCY_ADD_QUESTION, addVacancyQuestion),
    takeEvery(types.VACANCY_REMOVE_QUESTION, removeVacancyQuestion),
    takeEvery(types.VACANCY_GET_SUMMARY, getVacancySummary),
    takeEvery(types.VACANCY_GET_APPLICATIONS, getVacancyApplications),
    takeEvery(types.VACANCIES_POST_VACANCY, postVacancy),
    takeEvery(types.VACANCIES_PUT_ON_HOLD_VACANCY, putVacancyOnHold),
    takeEvery(types.VACANCIES_RE_POST_VACANCY, rePostVacancy),
    takeEvery(types.VACANCIES_ARCHIVE_VACANCY, archiveVacancy),
    takeEvery(types.VACANCIES_KEEP_POSTED_VACANCY, keepVacancyPosted),
    takeEvery(types.VACANCIES_REMAIN_ON_HOLD_VACANCY, remainVacancyOnHold),
    takeEvery(
      types.VACANCIES_GET_DATA_FOR_DUPLICATE_VACANCY,
      getDataForDuplicateVacancy
    ),
    takeEvery(
      types.VACANCY_GET_NOT_HIRED_APPLICATIONS_TO_INVITE_TO_DUPLICATED_VACANCY,
      getNotHiredApplicationsToInviteToDuplicatedVacancy
    ),
    takeEvery(types.VACANCIES_REMAIN_ON_HOLD_VACANCY, remainVacancyOnHold),
    takeEveryWithLoading(
      types.SCHOOL_SEND_PRIOR_CANDIDATES_INVITATIONS,
      sendPriorCandidatesInvitations,
      actions.setInvitingPriorCandidates
    ),
    takeEveryWithLoading(
      types.SCHOOL_SEND_BY_EMAILS_INVITATIONS,
      sendByEmailsInvitations,
      actions.setInvitingByEmails
    ),
    takeEvery(types.VACANCIES_DELETE_DRAFT_VACANCY, deleteDraftVacancy),
    takeEveryWithLoading(
      types.VACANCIES_PRINT_VACANCY_APPLICATIONS,
      printVacancyApplications,
      actions.setVacancyApplicationsPrintVersionIsLoading
    )
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/vacancies.js